/**
 * Per-team card and substitution derivations for the public share page's
 * score-bar header, alongside the existing goal-scorer list
 * (`useLiveScore.formatGoalLabel`).
 *
 * Card handling mirrors two guarded sources of truth rather than replaying
 * `MatchAnalytics.getNetCardTotalsByTeam` verbatim (that helper produces
 * per-team *counts*, not renderable rows, and lives in a guarded component):
 * - Chronological ordering uses the shared `compareCardEventOrder` from
 *   `../lib/clockHelpers` (also used by `useDisciplinary.ts`'s net-cards
 *   tracker), so a clock edit reorders the header the same way it reorders
 *   disciplinary state.
 * - The second-yellow auto-pair emitted by
 *   `useCockpitInteractionHandlers.ts` (`Card/"Yellow (Second)"` at
 *   globalClock+1ms immediately followed by `Card/"Red"` at +2ms) is
 *   collapsed into a single **merged** row (`kind: "secondYellow"`,
 *   rendered as two yellow chips + one red chip) at the second-yellow's
 *   own minute. The player's earlier standalone yellow row is absorbed
 *   (removed from the output) — its minute is retained on the merged row
 *   as `firstYellowMinute` so a client sees "this player had two yellows"
 *   on one line instead of two separate rows. The auto-paired Red event
 *   is still absorbed as before, never rendered on its own.
 * - `Card/"Cancelled"` events remove or revert a row using the same
 *   red-before-yellow priority as `getNetCardTotalsByTeam`'s cancel
 *   branch, extended to the merged row: if the player has an active
 *   dismissal row (a straight red, or a merged second-yellow row), the
 *   cancellation targets that row — a straight red row is removed
 *   entirely, while a merged row is *reverted* to a plain yellow row at
 *   its retained `firstYellowMinute` (one yellow still stands, matching
 *   `useDisciplinary.ts`'s red-priority cancel rule: red && yellow>=2 →
 *   removes one of each). Only when no dismissal row is active does a
 *   cancel remove the most recent standalone yellow row instead.
 */
import { formatDisplayName } from "../../../lib/nameFormat";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { compareCardEventOrder } from "../lib/clockHelpers";
import { getCardSubjectId, type Match } from "../types";

/**
 * Resolves a StaffMember.role to its display label. Optional so this file
 * stays a pure, dependency-free helper for unit tests — the real caller
 * (PublicMatchAnalytics.tsx, which has i18n access via useTranslation)
 * always supplies one, resolving "HEAD_COACH" to the same
 * logger.staffRoleHeadCoach key the cockpit uses and any other (free-text)
 * role through the shared admin.technicalStaffRoles map, so the same
 * person's role reads identically on both surfaces. When omitted, falls
 * back to a fixed Spanish default for "HEAD_COACH" (this page's historical
 * behavior before per-role translation existed) and the raw string for
 * anything else — never blank, never a crash.
 */
export type StaffRoleTranslator = (role: string) => string;

export type PublicCardKind = "yellow" | "red" | "secondYellow";

export interface PublicCardEntry {
  id: string;
  /**
   * Player (or staff) this row belongs to — the raw subject id, carried
   * alongside the already-rendered `playerLabel` so a consumer can key
   * per-person state off it without re-parsing the label. Added for
   * PMA-COCK-125's roster status marks (`publicRosterStatus.ts`), which
   * need the net card standing *per player* and must reuse this file's
   * cancel / second-yellow reduction rather than replay it.
   */
  subjectId: string;
  minute: string;
  /**
   * Wall-clock timestamp and numeric period of the event this row currently
   * represents — carried through so the public timeline
   * (`PublicMatchTimeline.tsx`) can retroactively reconstruct a stoppage-time
   * or halftime-break display minute when `minute` (the raw persisted
   * `match_clock`) is ambiguous. Kept in sync with `minute` through every
   * merge/cancel transform below (see `firstYellowTimestamp`/
   * `firstYellowPeriod`) — never left pointing at a different event's clock
   * reading than the one `minute` describes.
   */
  timestamp: string;
  period: number;
  kind: PublicCardKind;
  playerLabel: string;
  /**
   * Only set on a `secondYellow` merged row: the minute of the player's
   * absorbed first yellow, retained so a cancel of the dismissal can
   * revert the row to a plain yellow at the original minute.
   */
  firstYellowMinute?: string;
  /** Paired with `firstYellowMinute` — see that field's doc. */
  firstYellowTimestamp?: string;
  /** Paired with `firstYellowMinute` — see that field's doc. */
  firstYellowPeriod?: number;
}

export interface PublicSubstitutionEntry {
  id: string;
  /**
   * Raw player ids for the two sides of the swap, paired with the rendered
   * `offLabel`/`onLabel`. See `PublicCardEntry.subjectId` — same reason.
   * `null` when the event carries no id for that side (a malformed or
   * partially-recorded substitution), never an empty string, so a consumer
   * can't accidentally key a map on "".
   */
  offId: string | null;
  /** See `offId`. */
  onId: string | null;
  minute: string;
  /** See `PublicCardEntry.timestamp`/`.period`. */
  timestamp: string;
  period: number;
  offLabel: string;
  onLabel: string;
}

// Fallback used only when no translator is supplied (unit tests that don't
// care about i18n) — "HEAD_COACH" is the only non-free-text role value
// (MatchStaffRole is retired), so this is the only fixed fallback needed.
const DEFAULT_HEAD_COACH_LABEL = "DT";

const resolveStaffRoleLabel = (
  role: string,
  translateRole?: StaffRoleTranslator,
): string => {
  if (translateRole) return translateRole(role);
  return role === "HEAD_COACH" ? DEFAULT_HEAD_COACH_LABEL : role;
};

/**
 * Exported (2026-09-24, AC8 client wave "The Shot Flow Still Leaves the
 * Pitch...") so `PublicMatchAnalytics.tsx`'s live card-popup queue can
 * resolve a carded subject's label the EXACT same way this file's own
 * `derivePublicTeamCards` does (player or staff, same jersey/role
 * formatting) — reused, not re-implemented, so the popup and the header
 * list can never show a different name for the same card. Read-only reuse;
 * behaviour unchanged.
 */
export const resolvePlayerLabel = (
  match: Match,
  subjectId?: string | null,
  translateRole?: StaffRoleTranslator,
): string => {
  if (!subjectId) return "";
  const player =
    match.home_team.players.find((p) => p.id === subjectId) ||
    match.away_team.players.find((p) => p.id === subjectId);
  if (player)
    return `#${player.jersey_number} ${formatDisplayName(player.full_name)}`;

  const staff =
    match.home_team.technical_staff?.find((s) => s.staff_id === subjectId) ||
    match.away_team.technical_staff?.find((s) => s.staff_id === subjectId);
  if (staff) {
    const roleLabel = resolveStaffRoleLabel(staff.role, translateRole);
    return staff.name
      ? `${roleLabel} ${formatDisplayName(staff.name)}`
      : roleLabel;
  }

  return "";
};

/**
 * Exported alongside `resolvePlayerLabel` above (same 2026-09-24 AC8 wave)
 * so the live card-popup queue's Yellow(Second)+Red merge can reuse the
 * EXACT classification this file's own `derivePublicTeamCards` reduction
 * uses, rather than re-deriving a second opinion about what a raw
 * `card_type` string means.
 */
export type RawCardKind = "yellow" | "yellowSecond" | "red" | "cancelled";

export const classifyRawCardType = (cardType: string): RawCardKind | null => {
  if (cardType.includes("cancel")) return "cancelled";
  // A second yellow is a dismissal — collapsed into a red-equivalent row.
  if (cardType.includes("yellow (second)")) return "yellowSecond";
  if (cardType.includes("red")) return "red";
  if (cardType.includes("yellow")) return "yellow";
  return null;
};

interface PlayerCardRuntimeState {
  rows: PublicCardEntry[];
  pendingPairedRed: number;
}

export function derivePublicTeamCards(
  events: MatchEvent[],
  teamId: string,
  match: Match,
  translateRole?: StaffRoleTranslator,
): PublicCardEntry[] {
  const cardEvents = events
    .map((event, index) => ({ event, index }))
    .filter(
      ({ event }) =>
        event.type === "Card" &&
        event.team_id === teamId &&
        Boolean(getCardSubjectId(event)),
    )
    .sort(compareCardEventOrder);

  const entries: PublicCardEntry[] = [];
  const stateByPlayer = new Map<string, PlayerCardRuntimeState>();

  for (const { event } of cardEvents) {
    const subjectId = getCardSubjectId(event) as string;
    const raw = classifyRawCardType(
      String(event.data?.card_type || "").toLowerCase(),
    );
    if (!raw) continue;

    const state = stateByPlayer.get(subjectId) ?? {
      rows: [],
      pendingPairedRed: 0,
    };
    stateByPlayer.set(subjectId, state);

    if (raw === "cancelled") {
      const findLastIndexOfKinds = (kinds: PublicCardKind[]) => {
        for (let i = state.rows.length - 1; i >= 0; i -= 1) {
          if (kinds.includes(state.rows[i].kind)) return i;
        }
        return -1;
      };
      // Red-before-yellow priority, extended to the merged second-yellow
      // row (it *is* the dismissal, same as a straight red).
      const dismissalIndex = findLastIndexOfKinds(["red", "secondYellow"]);
      if (dismissalIndex !== -1) {
        const dismissalRow = state.rows[dismissalIndex];
        if (dismissalRow.kind === "red") {
          state.rows.splice(dismissalIndex, 1);
          const entryIndex = entries.indexOf(dismissalRow);
          if (entryIndex !== -1) entries.splice(entryIndex, 1);
        } else {
          // Revert the merged row to a plain yellow at the retained
          // first-yellow minute — one yellow still stands. timestamp/period
          // revert alongside minute so a downstream minute-recomputation
          // off timestamp+period lands on the original card, not the
          // absorbed dismissal.
          dismissalRow.kind = "yellow";
          dismissalRow.minute =
            dismissalRow.firstYellowMinute ?? dismissalRow.minute;
          dismissalRow.timestamp =
            dismissalRow.firstYellowTimestamp ?? dismissalRow.timestamp;
          dismissalRow.period =
            dismissalRow.firstYellowPeriod ?? dismissalRow.period;
          delete dismissalRow.firstYellowMinute;
          delete dismissalRow.firstYellowTimestamp;
          delete dismissalRow.firstYellowPeriod;
        }
      } else {
        const yellowIndex = findLastIndexOfKinds(["yellow"]);
        if (yellowIndex !== -1) {
          const [removed] = state.rows.splice(yellowIndex, 1);
          const entryIndex = entries.indexOf(removed);
          if (entryIndex !== -1) entries.splice(entryIndex, 1);
        }
      }
      continue;
    }

    if (raw === "red" && state.pendingPairedRed > 0) {
      // Absorbed into the preceding "Yellow (Second)" row — no new row.
      state.pendingPairedRed -= 1;
      continue;
    }

    if (raw === "yellowSecond") {
      state.pendingPairedRed += 1;

      // Absorb the player's earlier standalone yellow row into a single
      // merged row anchored at this (the second yellow's) minute.
      let firstYellowMinute: string | undefined;
      let firstYellowTimestamp: string | undefined;
      let firstYellowPeriod: number | undefined;
      for (let i = state.rows.length - 1; i >= 0; i -= 1) {
        if (state.rows[i].kind === "yellow") {
          const [absorbed] = state.rows.splice(i, 1);
          firstYellowMinute = absorbed.minute;
          firstYellowTimestamp = absorbed.timestamp;
          firstYellowPeriod = absorbed.period;
          const entryIndex = entries.indexOf(absorbed);
          if (entryIndex !== -1) entries.splice(entryIndex, 1);
          break;
        }
      }

      const mergedRow: PublicCardEntry = {
        id: event._id ?? event.client_id ?? `${event.timestamp}-${subjectId}`,
        subjectId,
        minute: event.match_clock,
        timestamp: event.timestamp,
        period: event.period,
        kind: "secondYellow",
        playerLabel:
          resolvePlayerLabel(match, subjectId, translateRole) || subjectId,
        ...(firstYellowMinute ? { firstYellowMinute } : {}),
        ...(firstYellowTimestamp ? { firstYellowTimestamp } : {}),
        ...(firstYellowPeriod !== undefined ? { firstYellowPeriod } : {}),
      };
      state.rows.push(mergedRow);
      entries.push(mergedRow);
      continue;
    }

    const row: PublicCardEntry = {
      id: event._id ?? event.client_id ?? `${event.timestamp}-${subjectId}`,
      subjectId,
      minute: event.match_clock,
      timestamp: event.timestamp,
      period: event.period,
      kind: raw === "yellow" ? "yellow" : "red",
      playerLabel:
        resolvePlayerLabel(match, subjectId, translateRole) || subjectId,
    };
    state.rows.push(row);
    entries.push(row);
  }

  return entries;
}

export function derivePublicTeamSubstitutions(
  events: MatchEvent[],
  teamId: string,
  match: Match,
  translateRole?: StaffRoleTranslator,
): PublicSubstitutionEntry[] {
  const entries: PublicSubstitutionEntry[] = [];
  for (const event of events) {
    if (event.type !== "Substitution" || event.team_id !== teamId) continue;
    entries.push({
      id:
        event._id ??
        event.client_id ??
        `${event.timestamp}-${event.data?.player_off_id}`,
      offId: event.data?.player_off_id ?? null,
      onId: event.data?.player_on_id ?? null,
      minute: event.match_clock,
      timestamp: event.timestamp,
      period: event.period,
      offLabel:
        resolvePlayerLabel(match, event.data?.player_off_id, translateRole) ||
        "?",
      onLabel:
        resolvePlayerLabel(match, event.data?.player_on_id, translateRole) ||
        "?",
    });
  }
  return entries;
}
